import type { QueryResult } from '@apollo/client';
import { QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { mapSignalIncidents } from 'src/graphql/entities/signal-incident/signal-incident.mappings';
import { GET_CONVERSATION_SIGNAL_INCIDENTS_QUERY } from 'src/graphql/entities/signal-incident/signal-incident.operations';
import type { SignalIncident } from 'src/graphql/entities/signal-incident/signal-incident.types';
import { type ConditionInput, type QueryParams, Records } from 'src/graphql/types';
import type { SignalIncidentRaw } from 'src/types';
import { sortByDate } from 'src/utils/sorting';

interface GetConversationSignalIncidentsQuery {
  getSignalIncident: Records<SignalIncidentRaw>;
}

interface GetConversationSignalIncidentsQueryVariables {
  params: QueryParams;
}

type UseConversationSignalIncidentsResult = Omit<
  QueryResult<
    GetConversationSignalIncidentsQuery,
    GetConversationSignalIncidentsQueryVariables
  >,
  'data'
> & {
  signalIncidents: SignalIncident[];
};

export function useConversationSignalIncidents(
  params: {
    conversationId?: string | null;
    partnerId?: number | null;
    skip?: boolean;
  } = {},
): UseConversationSignalIncidentsResult {
  const { conversationId, partnerId, skip = false } = params;

  const filters = useMemo<ConditionInput[]>(() => {
    const nextFilters: ConditionInput[] = [];

    if (conversationId) {
      nextFilters.push({
        attribute: 'conversationId',
        operator: '=',
        value: conversationId,
      });
    }

    if (typeof partnerId === 'number') {
      nextFilters.push({
        attribute: 'partnerId',
        operator: '=',
        value: String(partnerId),
      });
    }

    return nextFilters;
  }, [conversationId, partnerId]);

  const options = useMemo<
    QueryHookOptions<
      GetConversationSignalIncidentsQuery,
      GetConversationSignalIncidentsQueryVariables
    >
  >(
    () => ({
      skip: skip || !conversationId,
      variables: {
        params: {
          filters,
          sorting: { attribute: 'createdAt', desc: false },
        },
      },
    }),
    [conversationId, filters, skip],
  );

  const { data, ...rest } = useQuery<
    GetConversationSignalIncidentsQuery,
    GetConversationSignalIncidentsQueryVariables
  >(GET_CONVERSATION_SIGNAL_INCIDENTS_QUERY, options);

  const signalIncidents = useMemo<SignalIncident[]>(
    () =>
      mapSignalIncidents(data?.getSignalIncident.records).toSorted(
        sortByDate('createdAt', false),
      ),
    [data?.getSignalIncident.records],
  );

  return {
    signalIncidents,
    ...rest,
  };
}
